import React, { useState } from 'react'
import type { TravelCategory, ApprovalFlowNode } from '@/types'

// ─── SVG 图标 ────────────────────────────────────────────────────────────────

const IconPerson = () => (
  <svg className="w-6 h-6 text-orange-300" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z" />
  </svg>
)

const IconGroup = () => (
  <svg className="w-6 h-6 text-orange-400" viewBox="0 0 24 24" fill="currentColor">
    <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />
  </svg>
)

// ─── 工具函数 ─────────────────────────────────────────────────────────────────

/** 姓名·域账号，任一为空则只显示另一个 */
const formatPerson = (name?: string | null, id?: string | null) => {
  const n = name?.trim(), i = id?.trim()
  return (n && i) ? `${n}·${i}` : n || i || ''
}

/**
 * 将 operate 字符串映射为颜色 class 和展示文字
 * 百川返回值：同意 / 拒绝 / 撤回 / 转交 / 发起 / 已通过 等
 */
const resolveOperate = (operate?: string | null): { label: string; cls: string } | null => {
  if (!operate) return null
  const op = operate.trim()
  if (['同意', 'agree', 'AGREE', 'APPROVE', 'approved', '已通过'].includes(op)) {
    return { label: op === 'APPROVE' || op === 'agree' || op === 'approved' ? '同意' : op, cls: 'text-green-600' }
  }
  if (['拒绝', 'reject', 'REJECT', 'rejected'].includes(op)) {
    return { label: op === 'reject' || op === 'REJECT' || op === 'rejected' ? '拒绝' : op, cls: 'text-red-500' }
  }
  if (['撤回', 'revoke', 'REVOKE'].includes(op)) {
    return { label: '撤回', cls: 'text-yellow-600' }
  }
  if (['发起', 'start', 'START', 'submit'].includes(op)) {
    return { label: '发起', cls: 'text-green-600' }
  }
  // 其他值原样展示（灰色）
  return { label: op, cls: 'text-slate-400' }
}

// ─── 子组件 ───────────────────────────────────────────────────────────────────

/** 带头像图片或占位图标的圆形头像 */
const Avatar: React.FC<{ src?: string | null; size?: 'sm' | 'md'; children: React.ReactNode }> = (
  { src, size = 'md', children }
) => {
  const cls = size === 'sm'
    ? 'w-8 h-8 bg-slate-100'
    : 'w-10 h-10 bg-orange-100'
  return (
    <div className={`${cls} rounded-full flex items-center justify-center overflow-hidden flex-shrink-0`}>
      {src ? <img src={src} alt="" className="w-full h-full object-cover" /> : children}
    </div>
  )
}

/** 连接两个节点的竖线 */
const Connector = () => <div className="w-px flex-1 bg-orange-200 my-1 min-h-[24px]" />

/** operate 标签 + 时间（右侧列） */
const OperateLabel: React.FC<{ operate?: string | null; createdAt?: string | null }> = ({ operate, createdAt }) => {
  const resolved = resolveOperate(operate)
  if (!resolved && !createdAt) return null
  return (
    <div className="flex flex-col items-end flex-shrink-0 text-right min-w-[80px]">
      {resolved && (
        <span className={`text-sm font-medium ${resolved.cls}`}>{resolved.label}</span>
      )}
      {createdAt && (
        <span className="text-xs text-slate-400 mt-0.5 whitespace-nowrap">{createdAt}</span>
      )}
    </div>
  )
}

/** 普通单人审批节点 */
const SingleNode: React.FC<{ node: ApprovalFlowNode; isLast: boolean; isActual: boolean }> = ({ node, isLast, isActual }) => (
  <div className="flex gap-3">
    <div className="flex flex-col items-center flex-shrink-0">
      <Avatar src={node.userImg}><IconPerson /></Avatar>
      {!isLast && <Connector />}
    </div>
    <div className="flex-1 pb-4 flex justify-between items-start gap-2 min-w-0">
      <div className="min-w-0">
        <p className="text-sm text-slate-400 leading-5">{node.taskName}</p>
        <p className="text-base text-slate-500 leading-6 truncate">{formatPerson(node.userName, node.userId)}</p>
        {isActual && node.comment && (
          <p className="text-xs text-slate-400 mt-0.5 break-all">"{node.comment}"</p>
        )}
      </div>
      {isActual && (
        <OperateLabel operate={node.operate} createdAt={node.createdAt} />
      )}
    </div>
  </div>
)

/** 并行审批组节点（含 subTasks，如「同行人直属上级」） */
const GroupNode: React.FC<{ node: ApprovalFlowNode; isLast: boolean; isActual: boolean }> = ({ node, isLast, isActual }) => {
  const [expanded, setExpanded] = useState(true)
  const subTasks = node.subTasks ?? []

  // 计算组的整体 operate（所有子任务中若有拒绝则拒绝，全同意则同意）
  const groupOperate = isActual ? (() => {
    if (!subTasks.length) return node.operate
    const hasReject = subTasks.some(s => resolveOperate(s.operate)?.label === '拒绝')
    const allAgree = subTasks.every(s => resolveOperate(s.operate)?.label === '同意')
    if (hasReject) return '拒绝'
    if (allAgree) return '同意'
    return node.operate
  })() : undefined

  // 组的最新操作时间（取 updatedAt 最新的子节点）
  const groupTime = isActual ? (subTasks.map(s => s.createdAt).filter(Boolean).sort().at(-1) ?? node.createdAt) : undefined

  return (
    <div className="flex gap-3">
      <div className="flex flex-col items-center flex-shrink-0">
        <Avatar src={node.userImg}><IconGroup /></Avatar>
        {!isLast && <Connector />}
      </div>
      <div className="flex-1 pb-4">
        <div className="flex justify-between items-start gap-2">
          <div className="min-w-0">
            <p className="text-sm text-slate-400 leading-5">{node.taskName}</p>
            <p className="text-base text-slate-500 leading-6">需 {subTasks.length} 人全部通过</p>
          </div>
          {isActual && (
            <OperateLabel operate={groupOperate} createdAt={groupTime} />
          )}
        </div>

        <button
          type="button"
          className="text-sm text-blue-500 flex items-center gap-0.5 mt-1 mb-2"
          onClick={() => setExpanded(v => !v)}
        >
          {expanded ? '收起' : '查看审批人'}
          <svg
            className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`}
            viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>

        {expanded && (
          <div className="space-y-3 pl-1">
            {subTasks.map((sub, i) => (
              <div key={i} className="flex items-start gap-2">
                <Avatar src={sub.userImg} size="sm">
                  <svg className="w-5 h-5 text-slate-300" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z" />
                  </svg>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <span className="text-sm text-slate-500">{formatPerson(sub.userName, sub.userId)}</span>
                  {isActual && sub.comment && (
                    <p className="text-xs text-slate-400 mt-0.5 break-all">"{sub.comment}"</p>
                  )}
                </div>
                {isActual && (
                  <OperateLabel operate={sub.operate} createdAt={sub.createdAt} />
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

/** 流程结束节点（仅在 isActual 且 processEnded 时展示） */
const EndNode: React.FC<{ label: string; labelCls: string }> = ({ label, labelCls }) => (
  <div className="flex gap-3">
    <div className="flex flex-col items-center flex-shrink-0">
      <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
        <svg className="w-6 h-6 text-slate-400" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
        </svg>
      </div>
    </div>
    <div className="flex-1 pb-2 flex justify-between items-center">
      <p className="text-base text-slate-500 leading-6">流程结束</p>
      <span className={`text-sm font-medium ${labelCls}`}>{label}</span>
    </div>
  </div>
)

// ─── 主组件 ───────────────────────────────────────────────────────────────────

interface ApprovalPreviewProps {
  category: TravelCategory
  estimatedExpense?: number
  nodes?: ApprovalFlowNode[]
  isLoading?: boolean
  error?: string | null
  /**
   * true = 展示已启动流程的真实审批链（含 operate 状态、时间）
   * false = 仅展示预审批预览（无状态信息）
   */
  isActual?: boolean
  /** 应用当前状态，用于显示流程结束节点 */
  applicationStatus?: string | null
}

/**
 * 审批流程展示
 *
 * isActual=false：提交前预览，仅展示审批链人员结构
 * isActual=true：已提交后查看，展示每个节点的实际审批动作和时间
 */
const ApprovalPreview: React.FC<ApprovalPreviewProps> = ({
  category: _category,
  nodes,
  isLoading = false,
  error,
  isActual = false,
  applicationStatus,
}) => {
  const [collapsed, setCollapsed] = useState(false)

  const flow = nodes?.length ? nodes : []

  const title = isActual ? '审批流程' : '审批流程（预览）'

  // 判断是否展示"流程结束"节点
  const showEndNode = isActual && ['APPROVED', 'REJECTED', 'WITHDRAWN', 'TERMINATED'].includes(applicationStatus ?? '')
  const endLabel = (() => {
    if (applicationStatus === 'APPROVED') return { label: '已通过', cls: 'text-green-600' }
    if (applicationStatus === 'REJECTED') return { label: '已拒绝', cls: 'text-red-500' }
    if (applicationStatus === 'WITHDRAWN') return { label: '已撤回', cls: 'text-yellow-600' }
    if (applicationStatus === 'TERMINATED') return { label: '已终止', cls: 'text-slate-400' }
    return { label: '已结束', cls: 'text-slate-400' }
  })()

  return (
    <div className="clay-card">
      <div
        className="flex items-center justify-between cursor-pointer py-clay-md"
        onClick={() => setCollapsed(v => !v)}
      >
        <h3 className="text-clay-lg font-bold text-slate-900">{title}</h3>
        <span className="text-slate-400 text-lg">{collapsed ? '▶' : '▼'}</span>
      </div>

      {!collapsed && (
        <div className="mt-2">
          {isLoading ? (
            <p className="py-6 text-center text-sm text-slate-400">正在获取审批流程…</p>
          ) : flow.length > 0 ? (
            <div className="pt-2">
              {flow.map((node, i) =>
                node.subTasks?.length
                  ? <GroupNode key={i} node={node} isLast={i === flow.length - 1 && !showEndNode} isActual={isActual} />
                  : <SingleNode key={i} node={node} isLast={i === flow.length - 1 && !showEndNode} isActual={isActual} />
              )}
              {showEndNode && (
                <EndNode label={endLabel.label} labelCls={endLabel.cls} />
              )}
            </div>
          ) : (
            <p className="py-6 text-center text-sm text-slate-400">
              {isActual ? '暂无审批记录' : '暂无审批数据，新增或修改人员可触发重试'}
            </p>
          )}

          {error && !isLoading && (
            <div className="mt-3 p-3 rounded-lg bg-orange-50 border-l-4 border-orange-300">
              <p className="text-sm text-orange-700">⚠️ {error}</p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default ApprovalPreview
